// Entry point for the "pre" bundle.
//
// This module imports all UI components, sets up callbacks, initializes the page,
// and exposes the public API surface to `window.bms` (and legacy globals) so
// `bms-post.js` and inline handlers can call into it.

import {
  clearSearchFlagOnLoad,
  getOrCreateSessionId,
  isFirstQueryInSession,
  markSearchPerformed,
} from "../state/session.js";
import { getAuthHeaders } from "../api/auth.js";
import {
  clearActiveResearchIdOnServer,
  getCurrentResearchId,
  restoreActiveResearchIdFromStorage,
  setCurrentResearchId as _setCurrentResearchId,
} from "../state/research.js";
import { htmlToPlainText, replaceTimestampWithSearchId } from "../ui/html.js";
import { createCompareButton } from "../ui/compare-button.js";
import {
    formatCitationText,
    generateReferencesHTML,
    displayCitations,
    fetchAndDisplayCitations,
    getUserCitationFormat,
    setUserCitationFormat
} from "../ui/citations.js";
import { updateAssistantMessage, formatMessage } from "../ui/messages.js";
import { initializePublicationYearFilter } from "../ui/publication-year-filter.js";
import { showTools, resetTools } from "../ui/toolbar.js";
import { fetchSuggestions, createSuggestedChips } from "../ui/suggestions.js";
import { showSnackbar } from "../ui/snackbar.js";
import {
    toggleSidebar,
    openResearchHistory,
    openPreferences,
    closeResearchHistory,
    closePreferences,
    showMainSidebar,
    setSidebarCallbacks
} from "../ui/sidebar.js";
import { logResponse, goToPubMed, copyText, download } from "../ui/actions.js";
import { moveSearchBoxToBottom, moveHeaderBoxToTop } from "../ui/positioning.js";
import {
    openSignInModal,
    closeSignInModal,
    setPendingSearchQuery,
    getPendingSearchQuery,
    clearPendingSearchQuery
} from "../ui/signin-modal.js";
import {
    loadResearchHistory,
    createResearchSessionItem,
    deleteResearchSession,
    setResearchHistoryCallbacks
} from "../ui/research-history.js";
import { enableEditMode, revertToNormalMode, handleSearchWithQuery } from "../ui/edit-mode.js";
import {
    updateSignInUI,
    updateSignOutUI,
    checkAuthStatus,
    handleCognitoCallback,
    handleLogout,
    setAuthCallbacks
} from "../ui/auth.js";
import {
    loadPreferences,
    applyPreferences,
    setPreferencesCallbacks
} from "../ui/preferences.js";
import {
    loadResearchSession,
    waitForNewSearchButtonAndSave,
    saveSuggestionsHTML,
    setResearchSessionLoaderCallbacks
} from "../ui/research-session-loader.js";
import { initializePage, resetToInitialState } from "../ui/initialization.js";
import { handleSearch, checkSearchLimit } from "../ui/search-handler.js";

// Call this on page load to handle browser refresh
clearSearchFlagOnLoad();

// Pending search query management moved to ui/signin-modal.js
// Use the exported functions to access/modify pendingSearchQuery

// Restore last active research session id on load (if any)
restoreActiveResearchIdFromStorage();
// Bridge: preserve the legacy `currentResearchId` variable while moving the
// underlying state management into `state/research.js`.
let currentResearchId = getCurrentResearchId();

function setCurrentResearchId(newId) {
  _setCurrentResearchId(newId);
  currentResearchId = getCurrentResearchId();
}

// Set up sidebar callbacks after both loadResearchHistory and loadPreferences are defined
setSidebarCallbacks({
    loadResearchHistory,
    loadPreferences
});

// Set up research history callbacks
setResearchHistoryCallbacks({
    loadResearchHistory,
    loadResearchSession
});

// Set up preferences callbacks
setPreferencesCallbacks({
    loadResearchSession
});

// Set up research session loader callbacks
setResearchSessionLoaderCallbacks({
    handleSearch
});

// Initialize page with event listeners
initializePage({
    handleSearch,
    setCurrentResearchId
});

// -----------------------------------------------------------------------------
// Public API surface for the post bundle and for any inline handlers.
// Keep this at the very end so all referenced functions exist.
// -----------------------------------------------------------------------------
try {
  window.bms = window.bms || {};

  // Core flows used by other scripts
  if (typeof handleSearch === "function") window.bms.handleSearch = handleSearch;
  if (typeof checkSearchLimit === "function") window.bms.checkSearchLimit = checkSearchLimit;
  if (typeof moveSearchBoxToBottom === "function") window.bms.moveSearchBoxToBottom = moveSearchBoxToBottom;
  if (typeof adjustTextareaHeight === "function") window.bms.adjustTextareaHeight = adjustTextareaHeight;
  if (typeof loadResearchSession === "function") window.bms.loadResearchSession = loadResearchSession;
  if (typeof getCurrentResearchId === "function") window.bms.getCurrentResearchId = getCurrentResearchId;
  if (typeof toggleSidebar === "function") window.bms.toggleSidebar = toggleSidebar;

  // Functions used by inline onclick handlers
  // Wrap goToPubMed to pass currentResearchId
  if (typeof goToPubMed === "function") {
    window.bms.goToPubMed = (searchId) => goToPubMed(searchId, currentResearchId);
  }
  if (typeof download === "function") window.bms.download = download;
  if (typeof copyText === "function") window.bms.copyText = copyText;
  if (typeof logResponse === "function") window.bms.logResponse = logResponse;
  if (typeof updateAssistantMessage === "function") window.bms.updateAssistantMessage = updateAssistantMessage;

  // Back-compat globals (old code expects these to exist)
  if (window.bms.handleSearch) window.handleSearch = window.bms.handleSearch;
  if (window.bms.moveSearchBoxToBottom) window.moveSearchBoxToBottom = window.bms.moveSearchBoxToBottom;
  if (window.bms.adjustTextareaHeight) window.adjustTextareaHeight = window.bms.adjustTextareaHeight;
  if (window.bms.loadResearchSession) window.loadResearchSession = window.bms.loadResearchSession;
  if (window.bms.toggleSidebar) window.toggleSidebar = window.bms.toggleSidebar;

  // Expose functions for inline onclick handlers and inline scripts
  if (window.bms.goToPubMed) window.goToPubMed = window.bms.goToPubMed;
  if (window.bms.download) window.download = window.bms.download;
  if (window.bms.copyText) window.copyText = window.bms.copyText;
  if (window.bms.logResponse) window.logResponse = window.bms.logResponse;
  if (window.bms.updateAssistantMessage) window.updateAssistantMessage = window.bms.updateAssistantMessage;
} catch (e) {
  // Non-fatal: continue without exposing globals
  console.warn("[BMS] Failed to expose window.bms API", e);
}
